import {supabase} from '../../../lib/supabase';
import {getAuthenticatedActorId} from '../../../lib/api';
import {ReservationPreflightError,readPendingReservation,guardedLegacyReservation,startManualReservation,retryManualReservation,ManualIntent,PendingReservation} from '../../../services/manualReservationJournal';
import ContentPlanningPanel from '../ContentPlanningPanel';
import type {AllocationPreview} from '../../../services/contentPlanningService';
import React from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import {
  buildPostingCalendar,
  weeklyConsistency,
  proposeWeek,
  openDaysAhead,
  type ScheduledPost,
  type PublishedPost,
  type SchedulableReel,
  type ProposedSlot,
  slotChipLabel,
} from "../../../lib/postingCalendar";
import { counted, unknown, displayCount, type SocialsCount } from "../../../lib/socialsCounts";
import SocialsCard from "../SocialsCard";
import CaptionReviewDialog from "../CaptionReviewDialog";
import ScheduleDialog from "../ScheduleDialog";
import { publishSlotService, PublishSlotDto, CaptionDto } from "../../../services/publishSlotService";
import NeedsYouToday, { type TileReel, type TileThread } from "./NeedsYouToday";
import { humaniseTemplate } from "../../../lib/socialsFilters";
import type { CalendarMode } from "../../../lib/leadsCalendar";
import { shiftCalendarAnchor } from "../../../lib/leadsCalendar";

/**
 * Ch94 · OVERVIEW — "what needs you today", and the posting calendar.
 *
 * The calendar's grid is the LEADS calendar's grid (`buildCalendarGrid`,
 * extracted from `buildCalendar` unchanged), because Grant pointed at that page
 * and the packet says reuse it rather than grow a second one that drifts.
 *
 * 🔴 NOTHING ON THIS SCREEN SCHEDULES SILENTLY. "Auto-fill this week" produces
 * a PROPOSAL with a visible Confirm; dropping a reel on a day asks the same
 * way. The packet: "Grant confirms; nothing is scheduled silently."
 */

export interface OverviewCounts {
  toReview: SocialsCount;
  approvedWaiting: SocialsCount;
  conversations: SocialsCount;
}

interface Props {
  connected?: boolean;
  counts: OverviewCounts;
  scheduled: ScheduledPost[];
  /** `null` means content_posts has not been read — NOT that nothing posted. */
  posted: PublishedPost[] | null;
  readyToSchedule: SchedulableReel[];
  /**
   * Posts seen on Instagram we cannot match to a reel
   * (GET /admin/content/observations/unmatched). Unknown until read.
   */
  unmatched: SocialsCount;
  weeklyGoal: number;
  now?: Date;
  /** Absent ⇒ scheduling does not exist yet and the controls say so. */
  onSchedule?: (slots: ProposedSlot[]) => void;
  /** Schedule ONE named reel — opens the picker. */
  onScheduleOne?: (reviewId: string) => void;
  /** The next few reels waiting, for the needs-you tile. */
  reviewQueue?: TileReel[];
  /** The top conversations, for the needs-you tile. */
  threads?: TileThread[];
  onStartReviewing?: () => void;
  onOpenReels?: () => void;
  onOpenEngage?: () => void;
  onOpenThread?: (id: string) => void;
}

const OverviewTab: React.FC<Props> = ({
  counts,
  scheduled: suppliedScheduled,
  posted,
  readyToSchedule: suppliedReady,
  unmatched,
  weeklyGoal,
  now: suppliedNow,
  onSchedule: suppliedSchedule,
  onScheduleOne: suppliedScheduleOne,
  connected = false,
  reviewQueue = [],
  threads = [],
  onStartReviewing,
  onOpenReels,
  onOpenEngage, onOpenThread,
}) => {
  const [clock] = React.useState(() => new Date());
  const now = suppliedNow ?? clock;
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const [mode, setMode] = React.useState<CalendarMode>("month");
  const [anchor, setAnchor] = React.useState<Date>(now);
  const [proposal, setProposal] = React.useState<ProposedSlot[] | null>(null);
  const [picked, setPicked] = React.useState<SchedulableReel | null>(null);

  const [planning,setPlanning]=React.useState<{preview?:AllocationPreview;declarationsEnabled:boolean}>({declarationsEnabled:false});
  const [reservationActor,setReservationActor]=React.useState<string|null>(null);
  const actorRef=React.useRef<string|null>(null);
  const [atomicEnabled,setAtomicEnabled]=React.useState(false);
  const [pendingReservation,setPendingReservation]=React.useState<PendingReservation|null>(null);
  const [journalError,setJournalError]=React.useState<string|null>(null);
  React.useEffect(()=>{
    const refresh=()=>{if(actorRef.current!==reservationActor)return;try{setPendingReservation(reservationActor?readPendingReservation(reservationActor):null);setJournalError(null);}catch{setJournalError('The scheduling request journal could not be read. Preserve browser storage for reconciliation.');}};
    refresh();window.addEventListener('storage',refresh);window.addEventListener('content-reservation-journal',refresh);
    return ()=>{window.removeEventListener('storage',refresh);window.removeEventListener('content-reservation-journal',refresh);};
  },[reservationActor]);
  const [slots, setSlots] = React.useState<PublishSlotDto[]>([]);
  const [readyIds, setReadyIds] = React.useState<string[] | null>(null);
  const [slotError, setSlotError] = React.useState<string | null>(null);
  const [slotLoading, setSlotLoading] = React.useState(connected);
  const [slotBusy, setSlotBusy] = React.useState(false);
  const [unreadRows, setUnreadRows] = React.useState(0);
  const [slotNotice, setSlotNotice] = React.useState<string | null>(null);
  const lock = React.useRef(false);
  const readGeneration = React.useRef(0);
  const [reviewedCopy,setReviewedCopy] = React.useState<CaptionDto | null>(null);
  const [editing, setEditing] = React.useState<PublishSlotDto | null>(null);
  const [pickingTime, setPickingTime] = React.useState<SchedulableReel | null>(null);
  const [captionQueue, setCaptionQueue] = React.useState<ProposedSlot[]>([]);
  const [scheduleDraft, setScheduleDraft] = React.useState<ProposedSlot | null>(null);
  const [captionSingle, setCaptionSingle] = React.useState<SchedulableReel | null>(null);
  const [draggingSlot, setDraggingSlot] = React.useState<PublishSlotDto | null>(null);
  const composerEpochRef=React.useRef(0);
  const [composerEpoch,setComposerEpoch]=React.useState(0);
  const resetComposer=React.useCallback(()=>{
    setComposerEpoch(++composerEpochRef.current);
    setProposal(null);setPicked(null);setScheduleDraft(null);
    setEditing(null);setPickingTime(null);setCaptionSingle(null);setCaptionQueue([]);
    setReviewedCopy(null);setDraggingSlot(null);
  },[]);
  React.useEffect(()=>{
    if(!connected){resetComposer();actorRef.current=null;setReservationActor(null);setPendingReservation(null);setAtomicEnabled(false);return;}
    let active=true;let generation=0;
    const accept=(actor:string|null)=>{
      if(!active || actorRef.current===actor)return;
      actorRef.current=actor;readGeneration.current++;
      setReservationActor(actor);setPendingReservation(null);setJournalError(null);
      setAtomicEnabled(false);setSlots([]);setReadyIds(null);setSlotNotice(null);
      setPlanning({declarationsEnabled:false});resetComposer();
    };
    // Auth callbacks stay synchronous; Supabase may still hold its session lock.
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>{generation++;accept(session?.user?.id??null);});
    const initial=generation;
    void getAuthenticatedActorId().then(actor=>{if(active&&generation===initial)accept(actor);}).catch(()=>{if(active&&generation===initial){accept(null);setSlotError('Could not verify scheduling account. Sign in again before changing slots.');}});
    return ()=>{active=false;subscription.unsubscribe();actorRef.current=null;readGeneration.current++;composerEpochRef.current++;};
  },[connected,resetComposer]);
  const loadSlots = React.useCallback(async () => {
    if (!connected || !reservationActor || actorRef.current!==reservationActor) return;
    const generation = ++readGeneration.current;
    setSlotLoading(true); setSlotError(null); setPlanning({declarationsEnabled:false});
    try {
      // The read always covers today + 8 days too: auto-fill proposes from the
      // next week whichever month the calendar is showing (PKT-0912).
      const today = new Date(todayMs);
      const start = new Date(Math.min(new Date(anchor.getFullYear(), anchor.getMonth(), 1 - 7).getTime(), todayMs));
      const end = new Date(Math.max(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 8).getTime(), new Date(today.getFullYear(), today.getMonth(), today.getDate() + 8).getTime()));
      const [range, ready] = await Promise.all([publishSlotService.list(start, end), publishSlotService.approvedUnscheduled()]);
      if (generation === readGeneration.current) { setSlots(range.items); setUnreadRows(range.rejected_count ?? 0); setReadyIds(ready.items);setAtomicEnabled(ready.atomic_reservations?.enabled===true && ready.atomic_reservations.actor_id===reservationActor);setPlanning({preview:ready.allocation_preview,declarationsEnabled:ready.validity_declarations_enabled===true}); }
    } catch { if (generation === readGeneration.current) { setReadyIds(null); setSlotError("Could not read scheduling. Refresh before changing slots."); } }
    finally { if (generation === readGeneration.current) setSlotLoading(false); }
  }, [connected, anchor, todayMs, reservationActor]);
  React.useEffect(() => { void loadSlots(); return () => { readGeneration.current++; }; }, [loadSlots]);
  const scheduled: ScheduledPost[] = connected ? slots.filter(s => s.status !== "cancelled").map(s => ({
    reviewId:s.review_id, slotId:s.slot_id, scheduledFor:s.scheduled_for, status:s.status,
    subject:suppliedReady.find(r => r.reviewId === s.review_id)?.subject ?? s.review_id,
    template:suppliedReady.find(r => r.reviewId === s.review_id)?.template ?? "",
    resolutionCode:s.resolution_code, deliveryState:s.delivery_state,
  })) : suppliedScheduled;
  const readyToSchedule = connected ? suppliedReady.filter(r => readyIds?.includes(r.reviewId)) : suppliedReady;
  const mutate = async (action: () => Promise<unknown>, message: string) => {
    if (lock.current) return;
    lock.current = true; setSlotBusy(true); setSlotNotice(null);
    try { await action(); if(actorRef.current===reservationActor)setSlotNotice(message); }
    catch (error) { if(actorRef.current===reservationActor)setSlotNotice(error instanceof ReservationPreflightError ? error.message : reservationActor && (pendingReservation || atomicEnabled) ? "Could not confirm the change. Keep the saved request and use Retry same scheduling request to reconcile it." : "Could not confirm the change. The calendar has been re-read; check it before trying again."); }
    finally { await loadSlots(); lock.current = false; setSlotBusy(false); }
  };
  const manualChange=async(intent:ManualIntent,legacy:()=>Promise<PublishSlotDto>)=>{
    if(!reservationActor || actorRef.current!==reservationActor)throw new Error('Scheduling account changed.');
    return atomicEnabled?startManualReservation(reservationActor,intent):guardedLegacyReservation(reservationActor,legacy);
  };
  const canStartManualIntent=()=>{try{return Boolean(reservationActor && actorRef.current===reservationActor && !readPendingReservation(reservationActor) && !journalError);}catch{return false;}};
  const onSchedule = connected ? (proposals: ProposedSlot[]) => {if(canStartManualIntent()){resetComposer();setCaptionQueue(proposals);}} : suppliedSchedule;
  const onScheduleOne = connected ? (id: string) => {if(canStartManualIntent()){resetComposer();setCaptionSingle(readyToSchedule.find(r => r.reviewId === id) ?? null);}} : suppliedScheduleOne;
  const schedulingState = connected ? (slotLoading || slotError ? "not_connected" : "live") : (onSchedule ? "live" : "to_build");

  const calendar = React.useMemo(
    () => buildPostingCalendar(scheduled, posted ?? [], { anchor, mode, now }),
    [scheduled, posted, anchor, mode, now]
  );

  const consistency = React.useMemo(
    () => weeklyConsistency(posted, { weeks: 8, now }),
    [posted, now]
  );

  // PKT-0912: the next 7 open days ACROSS the month boundary, each with its
  // 9:00 still ahead — not the anchor month's remaining days, which on the last
  // day of a month after 09:00 was nothing, and auto-fill silently did nothing.
  const autoFillDays = openDaysAhead(scheduled, posted ?? [], now, 7)
    .filter((ms) => new Date(ms).setHours(9, 0, 0, 0) > now.getTime() + 300000)
    .slice(0, 7);
  const autoFillBlockedByNoDays =
    readyToSchedule.length > 0 && autoFillDays.length === 0 && !(connected && (slotLoading || slotError));

  /**
   * The legend lists the types actually on the calendar — derived, like every
   * other option on this tab. The mock hardcodes four; the real vocabulary is
   * open-ended, so a fixed key would be wrong the first time a type is added.
   */
  const calendarTypes = React.useMemo(() => {
    const seen = new Set<string>();
    calendar.weeks.flat().forEach((cell) => {
      cell.scheduled.forEach((s) => seen.add(s.template));
      cell.posted.forEach((p) => seen.add(p.template));
    });
    return Array.from(seen).filter(Boolean).sort();
  }, [calendar]);

  const place = (reel: SchedulableReel, dayMs: number) =>
    setProposal([{ reel, dayMs: new Date(new Date(dayMs).setHours(9, 0, 0, 0)).getTime() }]);

  return (
    <Stack spacing={3}>
      {connected && slotLoading ? <Alert severity="info">Reading scheduling…</Alert> : null}
      {slotError ? <Alert severity="warning" action={<Button onClick={() => void loadSlots()}>Refresh scheduling</Button>}>{slotError}</Alert> : null}
      {unreadRows ? <Alert severity="warning">{unreadRows} calendar rows could not be read. Other slots are shown.</Alert> : null}
      {connected && reservationActor && ((pendingReservation?.actor===reservationActor && pendingReservation) || journalError) ? <Alert severity="warning" action={pendingReservation && !journalError ? <Button disabled={!atomicEnabled || slotBusy || slotLoading || Boolean(slotError)} onClick={()=>void mutate(async()=>{const current=await retryManualReservation(reservationActor);setSlotNotice(`Request confirmed. Current slot status: ${current.status}.`);return current;},'Scheduling request reconciled; current calendar refreshed.')}>Retry same scheduling request</Button>:undefined}>
        {journalError ?? `Unconfirmed ${pendingReservation?.intent.operation} request. It may already have committed. Closing or leaving this page keeps the original request. Reconcile it before another change.`}
        {!atomicEnabled ? <Typography variant="body2">Reservation reconciliation is currently disabled. Your saved request is retained; other scheduling changes are blocked.</Typography>:null}
        {pendingReservation ? <Typography variant="body2">{pendingReservation.intent.operation==='create'?pendingReservation.intent.package_id:pendingReservation.intent.slot_id}{pendingReservation.intent.operation!=='cancel'?` · ${new Date(pendingReservation.intent.scheduled_for).toLocaleString()}`:''}</Typography>:null}
      </Alert>:null}
      {slotNotice ? <Alert severity="info">{slotNotice}</Alert> : null}
      {connected ? <Typography variant="caption">Times are shown in {Intl.DateTimeFormat().resolvedOptions().timeZone}. A consumed slot is not proof of a published post.</Typography> : null}
      <ContentPlanningPanel preview={connected?planning.preview:undefined} declarationsEnabled={connected&&planning.declarationsEnabled} reels={readyToSchedule} onRefresh={()=>void loadSlots()} />
      <CaptionReviewDialog key={`${reservationActor}:${composerEpoch}`} actorId={reservationActor??undefined} contextKey={String(composerEpoch)} packageId={pickingTime ? null : captionSingle?.reviewId ?? captionQueue[0]?.reel.reviewId ?? null}
        subject={captionSingle?.subject ?? captionQueue[0]?.reel.subject ?? ""}
        onCancel={resetComposer}
        onSaved={saved => {
          if(composerEpochRef.current!==composerEpoch || actorRef.current!==reservationActor || saved.package_id!==(captionSingle?.reviewId??captionQueue[0]?.reel.reviewId))return;
          setReviewedCopy(saved);
          if(captionSingle){setPickingTime(captionSingle);setCaptionSingle(null);setScheduleDraft(null);}
          else {const proposal=captionQueue[0];if(!proposal)return;setPickingTime(proposal.reel);setScheduleDraft(proposal);}
        }} />
      <ScheduleDialog initialWhen={editing ? new Date(editing.scheduled_for) : scheduleDraft ? new Date(scheduleDraft.dayMs) : undefined} movingReviewId={editing?.review_id} open={Boolean(editing || pickingTime)} now={suppliedNow} subject={pickingTime?.subject ?? editing?.review_id ?? ""}
        scheduled={scheduled.filter(s => s.status === undefined || s.status === "scheduled")} onCancel={resetComposer}
        onConfirm={slotBusy || (connected && (slotLoading || Boolean(slotError))) ? undefined : when => {
          if(composerEpochRef.current!==composerEpoch || actorRef.current!==reservationActor)return;
          const move=editing; const reel=pickingTime; const remaining=captionQueue.slice(1);
          setEditing(null);setPickingTime(null);setCaptionQueue([]);setScheduleDraft(null);
          void mutate(async () => {
            const result=move ? await manualChange({operation:'move',slot_id:move.slot_id,scheduled_for:when.toISOString()},()=>publishSlotService.move(move.slot_id,when,reservationActor??undefined)) : await manualChange({operation:'create',package_id:reel!.reviewId,scheduled_for:when.toISOString(),reviewed_fingerprint:reviewedCopy!.fingerprint,reviewed_caption_hash:reviewedCopy!.caption_hash},()=>publishSlotService.create(reel!.reviewId,when,reviewedCopy!,reservationActor??undefined));
            if(actorRef.current===reservationActor && composerEpochRef.current===composerEpoch)setCaptionQueue(remaining);return result;
          },move ? "Slot moved." : "Reel scheduled.");
        }} />
      <NeedsYouToday
        schedulingState={schedulingState}
        toReview={counts.toReview}
        reviewQueue={reviewQueue}
        onStartReviewing={onStartReviewing}
        approvedWaiting={connected ? (slotLoading || slotError ? unknown() : counted(readyToSchedule.length)) : counts.approvedWaiting}
        readyToSchedule={readyToSchedule}
        onScheduleOne={onScheduleOne}
        conversations={counts.conversations}
        threads={threads}
        onOpenEngage={onOpenEngage}
        onOpenThread={onOpenThread}
        instagramNote="Instagram: not set up yet"
      />

      {/*
        CALENDAR + RAIL, SIDE BY SIDE — the mock's proportions (roughly 2/3 · 1/3).
        Previously the calendar ran full width with the rail demoted to two text
        pills underneath, which is why the page read as an empty month.
      */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <SocialsCard
          title="Posting calendar"
          state={schedulingState}
          testId="posting-calendar-card"
          action={
            <Stack direction="row" gap={0.75} alignItems="center" sx={{flexWrap:"wrap", minWidth:0}}>
              <Button aria-label={`Previous ${mode}`} sx={{minHeight:44}} size="small" onClick={() => setAnchor((a) => shiftCalendarAnchor(a, mode, -1))}>
                ←
              </Button>
              <Typography variant="body2" data-testid="calendar-label">
                {calendar.label}
              </Typography>
              <Button aria-label={`Next ${mode}`} sx={{minHeight:44}} size="small" onClick={() => setAnchor((a) => shiftCalendarAnchor(a, mode, 1))}>
                →
              </Button>
              {(["month", "week"] as CalendarMode[]).map((value) => (
                <Chip
                  key={value}
                  size="small"
                  label={value === "month" ? "Month" : "Week"}
                  variant={mode === value ? "filled" : "outlined"}
                  sx={{minHeight:44}}
                  onClick={() => setMode(value)}
                />
              ))}
            </Stack>
          }
        >
          <Box
            data-testid="posting-calendar"
            sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <Typography key={d} variant="caption" color="text.secondary" align="center">
                {d}
              </Typography>
            ))}

            {calendar.weeks.flat().map((cell) => (
              <Box
                key={cell.ms}
                data-testid="calendar-day"
                role="button" tabIndex={0} aria-label={`Choose ${new Date(cell.ms).toLocaleDateString()}`}
                onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); if (picked) { place(picked, cell.ms); setPicked(null); } } }}
                onDragOver={(event) => {
                  if (picked || draggingSlot) event.preventDefault();
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggingSlot && !slotBusy) { const slot=draggingSlot; const time=new Date(slot.scheduled_for); const when=new Date(cell.ms); when.setHours(time.getHours(),time.getMinutes(),0,0); void mutate(() => manualChange({operation:'move',slot_id:slot.slot_id,scheduled_for:when.toISOString()},()=>publishSlotService.move(slot.slot_id,when,reservationActor??undefined)), "Slot moved."); setDraggingSlot(null); }
                  if (picked) place(picked, cell.ms);
                  setPicked(null);
                }}
                onClick={() => {
                  if (picked) {
                    place(picked, cell.ms);
                    setPicked(null);
                  }
                }}
                sx={{
                  minHeight: 62,
                  p: 0.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: cell.isToday ? "primary.main" : "divider",
                  bgcolor: picked && cell.inMonth ? "action.hover" : "transparent",
                  opacity: cell.inMonth ? 1 : 0.4,
                  cursor: picked ? "copy" : "default",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {cell.label}
                  {cell.isToday ? " · today" : ""}
                </Typography>

                {/* FILLED = posted. */}
                {cell.posted.map((post) => (
                  <Box
                    key={post.reviewId}
                    data-testid="calendar-posted"
                    sx={{
                      mt: 0.25,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      fontSize: 10,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {post.subject}
                  </Box>
                ))}

                {/* DASHED = scheduled, not out yet. */}
                {cell.scheduled.map((slot) => (
                  <Box
                    key={slot.slotId ?? slot.reviewId}
                    draggable={connected && slot.status === "scheduled" && !slotBusy}
                    onDragStart={event => {event.stopPropagation();setDraggingSlot(slots.find(s => s.slot_id === slot.slotId) ?? null);}}
                    onDragEnd={() => setDraggingSlot(null)}
                    data-testid="calendar-scheduled"
                    sx={{
                      mt: 0.25,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      border: "1px dashed",
                      borderColor: "primary.main",
                      color: "text.primary",
                      fontSize: 10,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {slotChipLabel(slot)}
                    {slot.status === "missed" ? " · MISSED — more than 2 hours late" : slot.status === "refused" ? ` · REFUSED: ${slot.resolutionCode}` : slot.status === "fired" ? ` · Consumed — ${slot.resolutionCode ?? "see posting record"}` : ["would_publish", "posting_off"].includes(slot.deliveryState ?? "") ? " · Would publish when armed and eligible" : null}
                    {connected && slot.status === "scheduled" ? <Stack direction="column" sx={{"& button":{minWidth:0,p:0.25,fontSize:10,whiteSpace:"nowrap",overflowWrap:"normal"}}}>
                      <Button size="small" disabled={slotBusy || slotLoading || Boolean(slotError)} onClick={event => {event.stopPropagation();setEditing(slots.find(s => s.slot_id === slot.slotId) ?? null);}}>Move</Button>
                      <Button size="small" disabled={slotBusy || slotLoading || Boolean(slotError)} onClick={event => {event.stopPropagation();void mutate(() => manualChange({operation:'cancel',slot_id:slot.slotId!},()=>publishSlotService.cancel(slot.slotId!,reservationActor??undefined)), "Slot unscheduled.");}}>Unschedule</Button>
                    </Stack> : null}
                  </Box>
                ))}

                {/* The drop target, so the affordance is visible before he drags. */}
                {picked && cell.inMonth && !cell.scheduled.length && !cell.posted.length ? (
                  <Typography variant="caption" color="primary.main" sx={{ fontSize: 10 }}>
                    drop a reel
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>

          {/*
            THE TYPE LEGEND, derived rather than hardcoded — the mock lists four
            fixed types; the real vocabulary is open-ended, so this shows the
            types actually present plus what dashed and filled mean.
          */}
          <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.5 }} alignItems="center">
            {calendarTypes.map((template) => (
              <Chip
                key={template}
                size="small"
                variant="outlined"
                label={humaniseTemplate(template)}
                sx={{ height: 20, fontSize: 10 }}
                data-testid="calendar-legend-type"
              />
            ))}
            <Typography variant="caption" color="text.secondary">
              dashed = scheduled · filled = posted
            </Typography>
          </Stack>

          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <Typography variant="caption" color="warning.main" data-testid="chip-gaps">
              {posted === null ? "Posting record not read — gaps are unknown" : `${calendar.gapCount} days this month with no post`}
            </Typography>
            <Typography variant="caption" color="text.secondary" data-testid="chip-unmatched">
              · {displayCount(unmatched).text} posts on Instagram we can't match to a reel
            </Typography>
          </Stack>
        </SocialsCard>

        <SocialsCard title="Ready to schedule" state={schedulingState} testId="ready-card">
          {readyToSchedule.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="ready-empty">
              {connected && (slotLoading || slotError) ? "Ready reels have not been read yet." : "Nothing is approved and waiting. Approve a reel and it shows up here."}
            </Typography>
          ) : (
            <Box data-testid="ready-rail">
              {readyToSchedule.map((reel) => (
                <Stack
                  key={reel.reviewId}
                  direction="row"
                  alignItems="center"
                  gap={1.25}
                  sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
                  data-testid="ready-reel-row"
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 34,
                      height: 44,
                      borderRadius: 1,
                      flexShrink: 0,
                      background: `linear-gradient(160deg, hsl(${
                        Array.from(reel.reviewId).reduce(
                          (h, c) => (h * 31 + c.charCodeAt(0)) % 360,
                          7
                        )
                      } 45% 38%), hsl(220 40% 22%))`,
                    }}
                  />
                  <Box
                    sx={{ minWidth: 0, flex: 1, cursor: "grab" }}
                    role="button" tabIndex={0} aria-label={`Pick ${reel.subject}`}
                    onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setPicked(picked?.reviewId === reel.reviewId ? null : reel); } }}
                    draggable
                    onDragStart={() => setPicked(reel)}
                    onClick={() =>
                      setPicked(picked?.reviewId === reel.reviewId ? null : reel)
                    }
                    data-testid="ready-reel"
                  >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {reel.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {humaniseTemplate(reel.template)}
                      {reel.durationLabel ? ` · ${reel.durationLabel}` : ""}
                    </Typography>
                  </Box>
                  {onScheduleOne ? (
                    <Button
                      size="small"
                      disabled={slotBusy || slotLoading}
                      onClick={() => onScheduleOne(reel.reviewId)}
                      data-testid="ready-pick-time"
                      sx={{ minWidth: 0, px: 1 }}
                    >
                      {/* ONE WORD for one act: the tile said "Schedule" and the
                          rail said "Pick a time" for the same thing. */}
                      Schedule
                    </Button>
                  ) : null}
                </Stack>
              ))}
            </Box>
          )}

          {picked ? (
            <Typography variant="caption" color="primary.main" data-testid="pick-hint" sx={{ mt: 1, display: "block" }}>
              Now pick a day for “{picked.subject}”.
            </Typography>
          ) : null}

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 1.5 }}
            disabled={readyToSchedule.length === 0 || autoFillDays.length === 0 || slotBusy || (connected && slotLoading)}
            onClick={() =>
              setProposal(proposeWeek(readyToSchedule, autoFillDays, { maxPerWeek: weeklyGoal }).map(s => ({...s,dayMs:new Date(new Date(s.dayMs).setHours(9,0,0,0)).getTime()})))
            }
            data-testid="auto-fill"
          >
            Auto-fill this week
          </Button>
          {autoFillBlockedByNoDays ? (
            <Typography variant="caption" color="warning.main" data-testid="auto-fill-empty" sx={{ mt: 1, display: "block" }}>
              No open days in the next week — every day already has something going out. Pick a day on the calendar instead.
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Spreads approved reels across open days, mixing types and buildings. You confirm
            before anything is scheduled.
          </Typography>

          {proposal && proposal.length > 0 ? (
            <Alert
              severity="info"
              sx={{ mt: 1.5 }}
              data-testid="proposal"
              action={
                <Stack direction="row" gap={1}>
                  <Button size="small" onClick={() => setProposal(null)}>
                    Discard
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!onSchedule || slotBusy || (connected && (slotLoading || Boolean(slotError)))}
                    onClick={() => {
                      onSchedule?.(proposal);
                      setProposal(null);
                    }}
                    data-testid="proposal-confirm"
                  >
                    {onSchedule ? "Schedule these" : "Scheduling isn't built yet"}
                  </Button>
                </Stack>
              }
            >
              {proposal.length} to schedule at 9:00 AM local time —{" "}
              {proposal
                .map(
                  (slot) =>
                    `${slot.reel.subject} on ${new Date(slot.dayMs).toLocaleDateString(undefined, {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}`
                )
                .join(" · ")}
            </Alert>
          ) : null}
        </SocialsCard>
      </Box>

      {/* CONSISTENCY + WHAT'S WORKING — the mock's two side-by-side cards. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <SocialsCard title={`Consistency · last 8 weeks`} state="to_build">
          <Typography variant="caption" color="text.secondary">
            goal {weeklyGoal} a week
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 1 }} alignItems="flex-end" data-testid="consistency">
            {consistency.map((week) => {
              const shown = displayCount(week.count);
              const height =
                week.count.kind === "counted" ? Math.min(week.count.value, weeklyGoal) : 0;
              return (
                <Stack key={week.weekStartMs} alignItems="center" sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 4 + (height / Math.max(weeklyGoal, 1)) * 48,
                      bgcolor: height > 0 ? "primary.main" : "action.hover",
                      borderRadius: 0.5,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {shown.text}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
          {/*
            🔴 THE COPY FOLLOWS THE DATA. It previously said "No reel has been
            posted yet" UNCONDITIONALLY whenever the record was readable — so a
            chart with a bar of 2 sat under a sentence saying nothing had ever
            posted. Three numbers disagreeing on one screen is exactly the kind
            of thing that makes a surface untrustworthy.
          */}
          {posted === null ? (
            <Typography variant="caption" color="text.secondary" data-testid="consistency-unread" sx={{ mt: 1, display: "block" }}>
              We haven't been able to read the posting record, so these are blank rather than zero.
            </Typography>
          ) : posted.length === 0 ? (
            <Typography variant="caption" color="text.secondary" data-testid="consistency-read" sx={{ mt: 1, display: "block" }}>
              No reel has been posted yet. The first scheduled post starts this chart.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" data-testid="consistency-some" sx={{ mt: 1, display: "block" }}>
              {posted.length} {posted.length === 1 ? "post" : "posts"} recorded in this window.
            </Typography>
          )}
        </SocialsCard>

        <SocialsCard title="What's working" state="not_connected" testId="whats-working">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Top reel by views
          </Typography>
          <Typography variant="caption" color="text.secondary">
            appears after the first posts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Views, reach and which reels pulled people in. This needs one more permission on the
            Instagram app that we haven't asked for yet, so there is nothing to show — not zero,
            just not connected.
          </Typography>
        </SocialsCard>
      </Box>
    </Stack>
  );
};

export default OverviewTab;
